import { GenePanel } from '@/@core/services/app.service'
import { unique } from '@/@core/utils/utils'
import {
  Autocomplete,
  Box,
  Button,
  darken,
  FormControl,
  FormControlLabel,
  FormGroup,
  lighten,
  Stack,
  styled,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import { InputNumber, Select } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import ChinahpoSearch from '../chinahpo-search'
import {
  ACMGClassification,
  inheritance,
  NObject,
  PICKER_GROUPS,
  ScipClingenDose,
  ScipGeneHits,
  TOKEN_All,
  VARIANT_ONTOLOGY
} from '../filter-variants'

interface Props {
  setSelectedKeys: any
  dataIndex: string
  genePanel: GenePanel[]
  handleListChange: any
  clearFilters: any
  confirm: any
  close: any
  handleReset: any
  analysisInfo: any
  setConfig: any
}

let geneName_List = [] as string[]

export const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'light'
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8)
}))

export const GroupItems = styled('ul')({
  padding: 0
})
const chromosome = [
  {
    text: 'chr1',
    value: '1'
  },
  {
    text: 'chr2',
    value: '2'
  },
  {
    text: 'chr3',
    value: '3'
  },
  {
    text: 'chr4',
    value: '4'
  },
  {
    text: 'chr5',
    value: '5'
  },
  {
    text: 'chr6',
    value: '6'
  },
  {
    text: 'chr7',
    value: '7'
  },
  {
    text: 'chr8',
    value: '8'
  },
  {
    text: 'chr9',
    value: '9'
  },
  {
    text: 'chr10',
    value: '10'
  },
  {
    text: 'chr11',
    value: '11'
  },
  {
    text: 'chr12',
    value: '12'
  },
  {
    text: 'chr13',
    value: '13'
  },
  {
    text: 'chr14',
    value: '14'
  },
  {
    text: 'chr15',
    value: '15'
  },
  {
    text: 'chr16',
    value: '16'
  },
  {
    text: 'chr17',
    value: '17'
  },
  {
    text: 'chr18',
    value: '18'
  },
  {
    text: 'chr19',
    value: '19'
  },
  {
    text: 'chr20',
    value: '20'
  },
  {
    text: 'chr21',
    value: '21'
  },
  {
    text: 'chr22',
    value: '22'
  },
  {
    text: 'chrX',
    value: 'X'
  },
  {
    text: 'chrY',
    value: 'Y'
  },
  {
    text: 'chrMT',
    value: 'MT'
  }
]

const sequenceOntology = Object.getOwnPropertyNames(VARIANT_ONTOLOGY)
  .map((key) => {
    return {
      title: key,
      label: <span>{key}</span>,
      options: VARIANT_ONTOLOGY[key as 'Exonic'].map((item) => {
        return { label: <span>{item.text}</span>, value: item.id }
      })
    }
  })
  .flat()

const tokenAll = PICKER_GROUPS.map((items, index) => {
  return {
    title: items.header,
    label: <span>{items.header}</span>,
    options: PICKER_GROUPS[index].children.map((item, index) => {
      return { label: <span>{item.token}</span>, value: item.label }
    })
  }
}).flat()

const FilterItem = (props: Props) => {
  const {
    dataIndex,
    setSelectedKeys,
    genePanel,
    clearFilters,
    confirm,
    handleReset,
    close
  } = props

  const { t } = useTranslation()

  const filterConfig = [
    {
      name: t('chromosome'),
      key: 'dbSNP',
      open: false,
      value: {
        chrom: [],
        posStart: '',
        posEnd: ''
      } as NObject
    },
    {
      name: t('gene'),
      key: 'gene',
      open: false,
      value: {
        genePanelSet: [],
        geneNameIn: [],
        geneNameOut: []
      } as NObject
    },
    {
      name: t('genetic-way'),
      key: 'inheritances',
      open: false,
      value: {
        inheritance: [] as string[]
      } as NObject
    },
    {
      name: t('Zygosity'),
      key: 'zygosity',
      open: false,
      value: {
        zygosity: ''
      } as NObject
    },
    {
      name: 'ROH',
      key: 'ROH',
      open: false,
      value: {
        is_ROH: []
      } as NObject
    },
    {
      name: t('phenotype'),
      key: 'phenotypes',
      open: false,
      value: {
        isUse: null,
        phenotypes: [] as string[],
        clinicalSynopsisAnd: [] as string[]
      } as NObject
    },
    {
      name: t('OMIM'),
      key: 'inheritance',
      open: false,
      value: {
        mimNumbers: [],
        inheritance: []
      } as NObject
    },
    {
      name: t('area'), // 无
      open: false,
      value: {
        geneSet: [] as string[]
      } as NObject
    },
    {
      name: t('Reads depth'),
      key: 'coverage',
      open: false,
      value: {
        readDepthOperator: 'gte',
        readDepthValue: 0,
        zygosity: '',
        qualMin: 0,
        qualMax: 0,
        alleleFrequencyOperator: 'gte',
        alleleFrequencyValue: 0
      } as NObject
    },
    {
      name: t('ACMG Score'),
      key: 'ACMG_priority',
      open: false,
      value: {
        acmgClassification: [],
        acmgScoreValue: null,
        acmgScoreOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
      } as NObject
    },
    {
      name: t('Index'),
      key: 'index',
      open: false,
      value: {
        ownerIndexMin: 0,
        ownerIndexMax: 0,
        indexMin: 0,
        indexMax: 0,
        organizationIndexMin: 0,
        organizationIndexMax: 0
      } as NObject
    },
    {
      name: t('Allele fraction'),
      key: 'indelOperator',
      open: false,
      value: {
        indelOperator: 'gte',
        indelValue: '',
        snvOperator: 'gte',
        snvValue: ''
      } as NObject
    },
    {
      name: t('Variation mass value'),
      key: 'qualMin',
      open: false,
      value: {
        qualMin: 0,
        qualMax: 0
      } as NObject
    },
    {
      name: t('Population frequency'),
      key: 'gnomAD_exome_ALL',
      open: false,
      value: {
        gnomAD: [],
        gnomADOperator: 'lte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        gnomADValue: ''
      } as NObject
    },

    {
      name: `GLS ${t('grading')}`,
      key: 'classification',
      open: false,
      value: {
        CLINSIG_final: null
      } as NObject
    },
    {
      name: t('CLINVAR_clnsig'),
      key: 'CLINVAR_clnsig_final',
      open: false,
      value: {
        CLINVAR_clnsig_final: null
      } as NObject
    },
    {
      name: t('F Score'),
      key: 'f_score',
      open: false,
      value: {
        fscoreValue: null,
        fscoreOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
      } as NObject
    },
    {
      name: t('G Score'),
      key: 'g_score',
      open: false,
      value: {
        gscoreValue: null,
        gscoreOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
      } as NObject
    },
    {
      name: t('CN'),
      key: 'CN',
      open: false,
      value: {
        CopyNumber: null
      } as NObject
    },
    {
      name: t('In-Silico predictions'),
      key: 'SpliceAI_max_score',
      open: false,
      value: {
        insilico: [],
        CADDValue: null,
        alphaMValue: null,
        alphaMOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        spliceAIValue: null,
        spliceAIOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        revelValue: null,
        revelOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
      } as NObject
    },
    {
      name: `ACMG ${t('rating')}`,
      key: 'variantScore',
      open: false,
      value: {
        acmgClassification: [],
        acmgScoreValue: null,
        acmgScoreOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
      } as NObject
    },
    {
      name: t('Variant option'),
      key: 'coding_effect',
      open: false,
      value: {
        sequenceOntology: [],
        userLocusIn: [],
        userLocusOut: []
      } as NObject
    },
    {
      name: t('Variant option'),
      key: 'tokenToLocations',
      open: false,
      value: {
        tokenToLocations: []
      } as NObject
    },
    {
      name: t('Variant Sources'),
      key: 'sampleSource',
      open: false,
      value: {
        sampleSource: []
      } as NObject
    },
    {
      name: `Clinvar ${t('Publication')}`,
      key: 'pubmed',
      open: false,
      value: {
        hasPubmed: []
      } as NObject
    },
    {
      name: `CNV ${t('Highlights')}`,
      key: 'scip_Highlights',
      open: false,
      value: {
        hasPositiveHighlight: false,
        hasNegativeHighlight: false
      } as NObject
    },
    {
      name: t('gnomAD SV & Clinvar SV'),
      key: 'scip_gnomAD&Clinvar',
      open: false,
      value: {
        scipOverlapRateValue: null,
        scipOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        scipGnomAdOverlapRateValue: null,
        scipGnomAdOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        scipClinvarOverlapRateValue: null,
        scipClinvarOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
      } as NObject
    },
    {
      name: t('Gene info'),
      key: 'scip_Geneinfo',
      open: false,
      value: {
        scipGeneOverlapRateValue: null,
        scipGeneOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        scipGenePliValue: null,
        scipGenePliOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        scipGeneLoeufValue: null,
        scipGeneLoeufOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
        scipGeneHiIn: [],
        scipGeneTsIn: []
      } as NObject
    },
    {
      name: t('Clingen'),
      key: 'scip_Clingen',
      open: false,
      value: {
        scipClingenHaploIn: [],
        scipClingenTriploIn: []
      } as NObject
    }
  ]
  const [config, setConfig] = React.useState(filterConfig)

  const [searchValue, setSearchValue] = React.useState('')

  const handleListChange = (
    value: any,
    name: string,
    items: { name: string },
    setSelectedKeys: any
  ) => {
    setSelectedKeys([value].flat())

    const list = config.map((item) => {
      if (item.name === items.name) {
        item.value[name] = value

        if (name === 'fscoreOperator' || name === 'gscoreOperator') {
          item.value[name] = value[0].operator
        } else if (
          (name === 'fscoreValue' || name === 'gscoreValue') &&
          value[0].value
        ) {
          item.value[name] = value[0].value
        } else if (name === 'chrom' && value[0].chrom.length) {
          item.value[name] = value[0].chrom
        } else if (name === 'posStart' && value[0].start) {
          item.value[name] = value[0].start
        } else if (name === 'CopyNumber' && value[0].CopyNumber) {
          item.value[name] = value[0].CopyNumber
        } else if (name === 'posEnd' && value[0].end) {
          item.value[name] = value[0].end
        } else if (name === 'alphaMValue' && value[0].alphaMValue) {
          item.value[name] = value[0].alphaMValue
        } else if (name === 'alphaMOperator') {
          item.value[name] = value[0].alphaMOperator
        } else if (name === 'acmgScoreValue' && value[0].acmgScoreValue) {
          item.value[name] = value[0].acmgScoreValue
        } else if (name === 'acmgScoreOperator') {
          item.value[name] = value[0].acmgScoreOperator
        } else if (name === 'acmgClassification') {
          item.value[name] = value[0].acmgClassification
        } else if (name === 'spliceAIValue' && value[0].spliceAIValue) {
          item.value[name] = value[0].spliceAIValue
        } else if (name === 'spliceAIOperator') {
          item.value[name] = value[0].spliceAIOperator
        } else if (name === 'revelValue' && value[0].revelValue) {
          item.value[name] = value[0].revelValue
        } else if (name === 'revelOperator') {
          item.value[name] = value[0].revelOperator
        } else if (name === 'zygosity') {
          item.value[name] = value[0].zygosity
        } else if (name === 'readDepthOperator') {
          item.value[name] = value[0].readDepthOperator
        } else if (name === 'readDepthValue' && value[0].readDepthValue) {
          item.value[name] = value[0].readDepthValue
        } else if (name === 'gnomADValue' && value[0].gnomADValue) {
          item.value[name] = value[0].gnomADValue
        } else if (name === 'gnomAD') {
          item.value[name] = value[0].gnomAD
        } else if (name === 'gnomADOperator') {
          item.value[name] = value[0].gnomADOperator
        } else if (name === 'mimNumbers') {
          item.value[name] = value[0].mimNumbers
        } else if (name === 'inheritance') {
          item.value[name] = value[0].inheritance
        } else if (name === 'alleleFrequencyOperator') {
          item.value[name] = value[0].alleleFrequencyOperator
        } else if (
          name === 'alleleFrequencyValue' &&
          value[0].alleleFrequencyValue
        ) {
          item.value[name] = value[0].alleleFrequencyValue
        } else if (name === 'qualMin' && value[0].qualMin) {
          item.value[name] = value[0].qualMin
        } else if (name === 'qualMax' && value[0].qualMax) {
          item.value[name] = value[0].qualMax
        } else if (name === 'ownerIndexMax' && value[0].ownerIndexMax) {
          item.value[name] = value[0].ownerIndexMax
        } else if (name === 'ownerIndexMin' && value[0].ownerIndexMin) {
          item.value[name] = value[0].ownerIndexMin
        } else if (name === 'indexMin' && value[0].indexMin) {
          item.value[name] = value[0].indexMin
        } else if (name === 'indexMax' && value[0].indexMax) {
          item.value[name] = value[0].indexMax
        } else if (
          name === 'organizationIndexMin' &&
          value[0].organizationIndexMin
        ) {
          item.value[name] = value[0].organizationIndexMin
        } else if (
          name === 'organizationIndexMax' &&
          value[0].organizationIndexMax
        ) {
          item.value[name] = value[0].organizationIndexMax
        } else if (name === 'hasPositiveHighlight') {
          console.log(
            'scip_Highlights_daa',
            value[0],
            item,
            item.value,
            item.value[name]
          )

          item.value[name] = value[0].hasPositiveHighlight
        } else if (name === 'hasNegativeHighlight') {
          console.log(
            'scip_Highlights_daa',
            value[0],
            item,
            item.value,
            item.value[name]
          )

          item.value[name] = value[0].hasNegativeHighlight
        } else if (
          name === 'scipOverlapRateValue' &&
          value[0].scipOverlapRateValue
        ) {
          item.value[name] = value[0].scipOverlapRateValue
        } else if (
          name === 'scipOverlapRateOperator' &&
          value[0].scipOverlapRateOperator
        ) {
          item.value[name] = value[0].scipOverlapRateOperator
        } else if (
          name === 'scipGnomAdOverlapRateValue' &&
          value[0].scipGnomAdOverlapRateValue
        ) {
          item.value[name] = value[0].scipGnomAdOverlapRateValue
        } else if (
          name === 'scipGnomAdOverlapRateOperator' &&
          value[0].scipGnomAdOverlapRateOperator
        ) {
          item.value[name] = value[0].scipGnomAdOverlapRateOperator
        } else if (
          name === 'scipClinvarOverlapRateValue' &&
          value[0].scipClinvarOverlapRateValue
        ) {
          item.value[name] = value[0].scipClinvarOverlapRateValue
        } else if (
          name === 'scipClinvarOverlapRateOperator' &&
          value[0].scipClinvarOverlapRateOperator
        ) {
          item.value[name] = value[0].scipClinvarOverlapRateOperator
        } else if (
          name === 'scipGeneOverlapRateValue' &&
          value[0].scipGeneOverlapRateValue
        ) {
          item.value[name] = value[0].scipGeneOverlapRateValue
        } else if (
          name === 'scipGeneOverlapRateOperator' &&
          value[0].scipGeneOverlapRateOperator
        ) {
          item.value[name] = value[0].scipGeneOverlapRateOperator
        } else if (name === 'scipGenePliValue' && value[0].scipGenePliValue) {
          item.value[name] = value[0].scipGenePliValue
        } else if (
          name === 'scipGenePliOperator' &&
          value[0].scipGenePliOperator
        ) {
          item.value[name] = value[0].scipGenePliOperator
        } else if (
          name === 'scipGeneLoeufValue' &&
          value[0].scipGeneLoeufValue
        ) {
          item.value[name] = value[0].scipGeneLoeufValue
        } else if (
          name === 'scipGeneLoeufOperator' &&
          value[0].scipGeneLoeufOperator
        ) {
          item.value[name] = value[0].scipGeneLoeufOperator
        } else if (name === 'scipGeneHiIn' && value[0].scipGeneHiIn.length) {
          item.value[name] = value[0].scipGeneHiIn
        } else if (name === 'scipGeneTsIn' && value[0].scipGeneTsIn.length) {
          item.value[name] = value[0].scipGeneTsIn
        } else if (
          name === 'scipClingenHaploIn' &&
          value[0].scipClingenHaploIn.length
        ) {
          item.value[name] = value[0].scipClingenHaploIn
        } else if (
          name === 'scipClingenTriploIn' &&
          value[0].scipClingenTriploIn.length
        ) {
          item.value[name] = value[0].scipClingenTriploIn
        }
      }

      return item
    })
    console.log(list)

    setConfig(list)
  }

  const obj: {
    value: null | number
    operator: string
  } = {
    value: 0,
    operator: 'gte'
  }

  const obj2: {
    start: number
    end: number
  } = {
    start: 0,
    end: 0
  }

  const obj3: {
    alphaMValue: any
    alphaMOperator: string
    spliceAIValue: any
    spliceAIOperator: string
    revelValue: any
    revelOperator: string
  } = {
    alphaMValue: 0,
    alphaMOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    spliceAIValue: 0,
    spliceAIOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    revelValue: 0,
    revelOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
  }

  const obj4: {
    readDepthValue: any
    readDepthOperator: string
    zygosity: string
    alleleFrequencyOperator: string
    alleleFrequencyValue: any
    qualMin: any
    qualMax: any
  } = {
    readDepthValue: 0,
    readDepthOperator: 'gte',
    zygosity: '',
    alleleFrequencyOperator: 'gte',
    alleleFrequencyValue: 0,
    qualMin: 0,
    qualMax: 0
  }

  const obj5: {
    start: any
    end: any
    chrom: string[]
  } = {
    start: 0,
    end: 0,
    chrom: []
  }

  const obj6: {
    gnomADValue: any
    gnomADOperator: any
    gnomAD: string[]
  } = {
    gnomAD: [],
    gnomADOperator: 'lte',
    gnomADValue: ''
  }

  const obj7: {
    mimNumbers: any[]
    inheritance: any[]
  } = {
    mimNumbers: [],
    inheritance: []
  }

  const obj8: {
    CopyNumber: number
  } = {
    CopyNumber: 0
  }

  const obj9: {
    Min: any
    Max: any
  } = {
    Min: 0,
    Max: 0
  }

  const obj10: {
    ownerIndexMin: any
    ownerIndexMax: any
    indexMin: any
    indexMax: any
    organizationIndexMin: any
    organizationIndexMax: any
  } = {
    ownerIndexMin: 0,
    ownerIndexMax: 0,
    indexMin: 0,
    indexMax: 0,
    organizationIndexMin: 0,
    organizationIndexMax: 0
  }

  const obj11: {
    Min: any
    Max: any
  } = {
    Min: 0,
    Max: 0
  }

  const obj12: {
    hasPositiveHighlight: boolean
    hasNegativeHighlight: boolean
  } = {
    hasPositiveHighlight: false,
    hasNegativeHighlight: false
  }

  const obj13: {
    scipOverlapRateValue: any
    scipOverlapRateOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
    scipGnomAdOverlapRateValue: any
    scipGnomAdOverlapRateOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
    scipClinvarOverlapRateValue: any
    scipClinvarOverlapRateOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
  } = {
    scipOverlapRateValue: null,
    scipOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    scipGnomAdOverlapRateValue: null,
    scipGnomAdOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    scipClinvarOverlapRateValue: null,
    scipClinvarOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
  }

  const obj14: {
    scipGeneOverlapRateValue: any
    scipGeneOverlapRateOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
    scipGenePliValue: any
    scipGenePliOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
    scipGeneLoeufValue: any
    scipGeneLoeufOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
    scipGeneHiIn: any[]
    scipGeneTsIn: any[]
  } = {
    scipGeneOverlapRateValue: null,
    scipGeneOverlapRateOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    scipGenePliValue: null,
    scipGenePliOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    scipGeneLoeufValue: null,
    scipGeneLoeufOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.',
    scipGeneHiIn: [],
    scipGeneTsIn: []
  }

  const obj15: {
    scipClingenHaploIn: any[]
    scipClingenTriploIn: any[]
  } = {
    scipClingenHaploIn: [],
    scipClingenTriploIn: []
  }

  const obj16: {
    acmgClassification: any[]
    acmgScoreValue: any
    acmgScoreOperator: 'lte' | 'gte' | 'lte.' | 'gte.'
  } = {
    acmgClassification: [],
    acmgScoreValue: null,
    acmgScoreOperator: 'gte' as 'lte' | 'gte' | 'lte.' | 'gte.'
  }

  const items = config.find((item) => item.key === dataIndex) ?? config[0]

  //

  return (
    <Box>
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        {dataIndex === 'gene' ? (
          <FormControl sx={{ m: 1, width: '100%' }} size="small">
            <Select
              placeholder={t('Choose gene panel')}
              optionFilterProp="label"
              onChange={(value: string[]) => {
                if (!value.length)
                  handleListChange(
                    Array.from(new Set([])),
                    'geneNameIn',
                    items,
                    setSelectedKeys
                  )
                else {
                  const geneNames = value
                    .map((geneNames: string) => geneNames.split(','))
                    .flat()

                  geneNames.map((item: string) => {
                    if (!geneName_List.some((i) => i === item))
                      geneName_List.push(item)
                  })

                  handleListChange(
                    Array.from(
                      new Set([...(items.value.geneNameIn ?? []), ...geneNames])
                    ),
                    'geneNameIn',
                    items,
                    setSelectedKeys
                  )
                }
              }}
              mode="multiple"
              allowClear
              options={genePanel.map((item) => {
                return {
                  value: item.geneNames,
                  label: item.panelName
                }
              })}
            />

            <Select
              placeholder={t('Enter gene name')}
              optionFilterProp="label"
              onChange={(value: string[]) => {
                handleListChange(
                  value.map((item: any) => item),
                  'geneNameIn',
                  items,
                  setSelectedKeys
                )

                setSearchValue('')

                geneName_List = []
              }}
              mode="multiple"
              allowClear
              value={items.value.geneNameIn ?? []}
              options={geneName_List.map((item) => {
                return {
                  value: item,
                  label: item
                }
              })}
              searchValue={searchValue}
              onSearch={(e) => {
                const arrValue = e.split(' ').join(',').split(',')

                setSearchValue(e)

                arrValue
                  .filter((item) => item)
                  .forEach((item) => {
                    if (geneName_List.find((i) => i === item)) {
                      //
                    } else {
                      geneName_List.push(item.toUpperCase())
                    }
                  })

                if (e.includes(',') || e.includes(' ')) {
                  handleListChange(
                    Array.from(
                      new Set([
                        ...arrValue.filter((item) => item),
                        ...(items?.value.geneNameIn ?? [])
                      ])
                    ),
                    'geneNameIn',
                    items,
                    setSelectedKeys
                  )

                  setSearchValue('')
                }
              }}
              onInputKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleListChange(
                    Array.from(
                      new Set([
                        geneName_List[geneName_List.length - 1].toUpperCase(),
                        ...(items.value.geneNameIn ?? [])
                      ])
                    ).filter((item) => item),
                    'geneNameIn',
                    items,
                    setSelectedKeys
                  )

                  setSearchValue('')

                  geneName_List = []
                }
              }}
              maxTagCount={14}
            />
          </FormControl>
        ) : dataIndex === 'coding_effect' ? (
          <FormControl sx={{ m: 1, width: '100%' }} size="small">
            <Select
              placeholder={t('Sequence ontology')}
              optionFilterProp="label"
              onChange={(value: string[]) => {
                handleListChange(
                  value,
                  'sequenceOntology',
                  items,
                  setSelectedKeys
                )

                geneName_List = []
              }}
              mode="multiple"
              allowClear
              value={items.value.sequenceOntology ?? []}
              options={sequenceOntology}
              maxTagCount={4}
            />
          </FormControl>
        ) : dataIndex === 'tokenToLocations' ? (
          <FormControl sx={{ m: 1, width: '100%' }} size="small">
            <Select
              placeholder={t('Sequence ontology')}
              optionFilterProp="label"
              onChange={(value: string[]) => {
                handleListChange(
                  value,
                  'tokenToLocations',
                  items,
                  setSelectedKeys
                )

                geneName_List = []
              }}
              mode="multiple"
              allowClear
              value={items.value.tokenToLocations ?? []}
              options={tokenAll}
              maxTagCount={4}
            />
          </FormControl>
        ) : dataIndex === 'scip_Highlights' ? (
          <FormControl sx={{ m: 1, width: '100%' }} size="small">
            <FormGroup>
              <FormControlLabel
                onChange={(e: any, value) => {
                  obj12.hasPositiveHighlight = value

                  obj12.hasNegativeHighlight = items.value.hasNegativeHighlight

                  //

                  handleListChange(
                    [obj12],
                    'hasPositiveHighlight',
                    items,
                    setSelectedKeys
                  )
                }}
                control={<Switch checked={items.value.hasPositiveHighlight} />}
                label="Positive"
                labelPlacement="start"
                sx={{
                  justifyContent: 'start'
                }}
              />

              <FormControlLabel
                onChange={(e: any, value) => {
                  obj12.hasNegativeHighlight = value

                  //

                  obj12.hasPositiveHighlight = items.value.hasPositiveHighlight

                  handleListChange(
                    [obj12],
                    'hasNegativeHighlight',
                    items,
                    setSelectedKeys
                  )
                }}
                control={<Switch checked={items.value.hasNegativeHighlight} />}
                label="Negative"
                labelPlacement="start"
                sx={{
                  justifyContent: 'start'
                }}
              />
            </FormGroup>
          </FormControl>
        ) : dataIndex === 'inheritance' ? (
          <FormControl sx={{ m: 1, width: '100%' }} size="small">
            <Box>
              <ChinahpoSearch
                termList={items.value.mimNumbers}
                fn={(query: any) => {
                  //
                  obj7.mimNumbers = query
                  obj7.inheritance = items.value.inheritance
                  console.log(obj7)

                  handleListChange([obj7], 'mimNumbers', items, setSelectedKeys)

                  return query
                }}
                sx={{
                  flex: 1,
                  width: '100%',
                  '& .MuiAutocomplete-option': {
                    fontSize: '12px !important'
                  }
                }}
                sampleId={3272}
                disabledHp
                disabledRefersh
              />
            </Box>

            <Box pt={2}>
              <ToggleButtonGroup
                aria-label="device"
                size="small"
                value={items.value.inheritance}
                onChange={(e, value) => {
                  obj7.inheritance = value

                  obj7.mimNumbers = items.value.mimNumbers

                  handleListChange(
                    [obj7],
                    'inheritance',
                    items,
                    setSelectedKeys
                  )
                }}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  width: '100%',
                  justifyContent: 'center',
                  '& .Mui-selected': {
                    borderColor: `${'rgb(46, 125, 50)'} !important`,
                    backgroundColor: `${'rgb(46, 125, 50,0.6)'} !important`
                  }
                }}
              >
                {inheritance.map((item) => {
                  return (
                    <ToggleButton
                      sx={{
                        textTransform: 'none'
                      }}
                      value={item}
                      aria-label={item}
                    >
                      {item}
                    </ToggleButton>
                  )
                })}
              </ToggleButtonGroup>
            </Box>
          </FormControl>
        ) : dataIndex === 'coverage' ? (
          <FormControl sx={{ m: 1, width: '340px' }} size="small">
            <Stack direction="column" spacing={1.5} alignItems="stretch">
              {/* Allele Frequency */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 100 }}>
                  {t('allele Frequency')}
                </Typography>

                <Select
                  size="small"
                  value={items.value.alleleFrequencyOperator}
                  onChange={(e) =>
                    handleListChange(
                      [{ ...items.value, alleleFrequencyOperator: e }],
                      'alleleFrequencyOperator',
                      items,
                      setSelectedKeys
                    )
                  }
                  options={[
                    { value: 'gte', label: '≥' },
                    { value: 'lte', label: '≤' }
                  ]}
                />

                <InputNumber
                  size="small"
                  value={items.value.alleleFrequencyValue}
                  placeholder={t('allele Frequency')}
                  onChange={(e) => {
                    if (e != null) {
                      handleListChange(
                        [{ ...items.value, alleleFrequencyValue: e }],
                        'alleleFrequencyValue',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                />
              </Stack>

              {/* QUAL Range */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
              >
                <InputNumber
                  size="small"
                  value={items.value.qualMin}
                  placeholder={t('qualMin')}
                  onChange={(e) => {
                    if (e != null) {
                      handleListChange(
                        [{ ...items.value, qualMin: e }],
                        'qualMin',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                />

                <Typography variant="body2" sx={{ px: 1 }}>
                  ≤ QUAL ≤
                </Typography>

                <InputNumber
                  size="small"
                  value={items.value.qualMax}
                  placeholder={t('qualMax')}
                  onChange={(e) => {
                    if (e != null) {
                      handleListChange(
                        [{ ...items.value, qualMax: e }],
                        'qualMax',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                />
              </Stack>

              {/* Reads Depth */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 100 }}>
                  {t('Reads depth')}
                </Typography>

                <Select
                  size="small"
                  value={items.value.readDepthOperator}
                  onChange={(e) =>
                    handleListChange(
                      [{ ...items.value, readDepthOperator: e }],
                      'readDepthOperator',
                      items,
                      setSelectedKeys
                    )
                  }
                  options={[
                    { value: 'gte', label: '≥' },
                    { value: 'lte', label: '≤' }
                  ]}
                />

                <InputNumber
                  size="small"
                  value={items.value.readDepthValue}
                  placeholder={t('Reads depth')}
                  onChange={(e) => {
                    if (e != null) {
                      handleListChange(
                        [{ ...items.value, readDepthValue: e }],
                        'readDepthValue',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                />
              </Stack>

              {/* Zygosity */}
              <ToggleButtonGroup
                size="small"
                value={items.value.zygosity}
                exclusive
                onChange={(e, value) =>
                  handleListChange(
                    [{ ...items.value, zygosity: value }],
                    'zygosity',
                    items,
                    setSelectedKeys
                  )
                }
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  '& .Mui-selected': {
                    borderColor: 'rgb(46, 125, 50) !important',
                    backgroundColor: 'rgba(46, 125, 50, 0.15) !important'
                  }
                }}
              >
                <ToggleButton
                  value=""
                  aria-label={t('any')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('any')}
                </ToggleButton>

                <ToggleButton
                  value="homozygote"
                  aria-label={t('homozygote')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('homozygote')}
                </ToggleButton>

                <ToggleButton
                  value="heterozygote"
                  aria-label={t('heterozygote')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('heterozygote')}
                </ToggleButton>

                <ToggleButton
                  value="compound hets"
                  aria-label={t('recombination')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('recombination')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </FormControl>
        ) : dataIndex === 'ACMG_priority' ? (
          <FormControl sx={{ m: 1, width: '400px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <Stack direction={'row'} spacing={2} alignItems={'center'}>
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('ACMG Score')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('ACMG Score')}
                    value={items.value.acmgScoreOperator}
                    onChange={(e) => {
                      obj16.acmgScoreOperator = e

                      obj16.acmgScoreValue = items.value.acmgScoreValue

                      obj16.acmgClassification = items.value.acmgClassification

                      handleListChange(
                        [obj16],
                        'acmgScoreOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                  <InputNumber
                    value={items.value.acmgScoreValue}
                    placeholder="acmgScoreValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj16.acmgScoreValue = value

                        obj16.acmgScoreOperator = items.value.acmgScoreOperator

                        obj16.acmgClassification =
                          items.value.acmgClassification

                        handleListChange(
                          [obj16],
                          'acmgScoreValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                  />
                </FormControl>
              </Stack>

              <FormControl sx={{ m: 1, width: '10rem' }} size="small">
                <Select
                  optionFilterProp="label"
                  placeholder={t('ACMG Classification')}
                  onChange={(value: string[]) => {
                    obj16.acmgClassification = value

                    obj16.acmgScoreValue = items.value.acmgScoreValue

                    obj16.acmgScoreOperator = items.value.acmgScoreOperator

                    handleListChange(
                      [obj16],
                      'acmgClassification',
                      items,
                      setSelectedKeys
                    )
                  }}
                  mode="multiple"
                  allowClear
                  value={items.value.acmgClassification}
                  options={ACMGClassification.map((item) => {
                    return {
                      value: item.key,
                      label: item.title
                    }
                  })}
                />
              </FormControl>

              {/* <ToggleButtonGroup
                aria-label="device"
                size="small"
                value={items.value.acmgClassification}
                onChange={(e, value) => {
                  obj16.acmgClassification = value

                  obj16.acmgScoreValue = items.value.acmgScoreValue

                  obj16.acmgScoreOperator = items.value.acmgScoreOperator

                  handleListChange(
                    [obj16],
                    'acmgClassification',
                    items,
                    setSelectedKeys
                  )
                }}
                exclusive
              >
                {ACMGClassification.map((item) => {
                  return (
                    <ToggleButton key={item.key} value={item.key}>
                      {item.title}
                    </ToggleButton>
                  )
                })}
              </ToggleButtonGroup> */}
            </Stack>
          </FormControl>
        ) : dataIndex === 'index' ? (
          <FormControl sx={{ m: 1, width: '400px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl
                sx={{
                  m: 1,
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
                size="small"
              >
                <Stack direction={'column'} spacing={2}>
                  <Stack direction={'row'} spacing={2} alignItems="center">
                    <Stack direction={'row'}>
                      <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                        <InputNumber
                          value={items.value.organizationIndexMin}
                          placeholder="organizationIndexMin"
                          onChange={(e) => {
                            if (e) {
                              const value = e

                              obj10.organizationIndexMin = value

                              obj10.ownerIndexMin = items.value.ownerIndexMin

                              obj10.ownerIndexMax = items.value.ownerIndexMax

                              obj10.indexMin = items.value.indexMin

                              obj10.indexMax = items.value.indexMax

                              obj10.organizationIndexMax =
                                items.value.organizationIndexMax

                              handleListChange(
                                [obj10],
                                'organizationIndexMin',
                                items,
                                setSelectedKeys
                              )
                            }
                          }}
                        />
                      </FormControl>
                    </Stack>

                    <FormControl sx={{ m: 1 }} size="small">
                      <Typography>{` ≤ ${t(
                        'organization'
                      )} Index ≤ `}</Typography>
                    </FormControl>

                    <Stack direction={'row'}>
                      <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                        <InputNumber
                          value={items.value.organizationIndexMax}
                          placeholder="organizationIndexMax"
                          onChange={(e) => {
                            if (e) {
                              const value = e

                              obj10.organizationIndexMax = value

                              obj10.ownerIndexMin = items.value.ownerIndexMin

                              obj10.ownerIndexMax = items.value.ownerIndexMax

                              obj10.indexMin = items.value.indexMin

                              obj10.indexMax = items.value.indexMax

                              obj10.organizationIndexMin =
                                items.value.organizationIndexMin

                              handleListChange(
                                [obj10],
                                'organizationIndexMax',
                                items,
                                setSelectedKeys
                              )
                            }
                          }}
                        />
                      </FormControl>
                    </Stack>
                  </Stack>

                  <Stack direction={'row'} spacing={2} alignItems="center">
                    <Stack direction={'row'}>
                      <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                        <InputNumber
                          value={items.value.ownerIndexMin}
                          placeholder="ownerIndexMin"
                          onChange={(e) => {
                            if (e) {
                              const value = e

                              obj10.ownerIndexMin = value

                              obj10.ownerIndexMax = items.value.ownerIndexMax

                              obj10.indexMin = items.value.indexMin

                              obj10.indexMax = items.value.indexMax

                              obj10.organizationIndexMin =
                                items.value.organizationIndexMin

                              obj10.organizationIndexMax =
                                items.value.organizationIndexMax

                              handleListChange(
                                [obj10],
                                'ownerIndexMin',
                                items,
                                setSelectedKeys
                              )
                            }
                          }}
                        />
                      </FormControl>
                    </Stack>

                    <FormControl sx={{ m: 1 }} size="small">
                      <Typography>{` ≤ ${t('owner')} Index ≤ `}</Typography>
                    </FormControl>

                    <Stack direction={'row'}>
                      <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                        <InputNumber
                          value={items.value.ownerIndexMax}
                          placeholder="ownerIndexMax"
                          onChange={(e) => {
                            if (e) {
                              const value = e

                              obj10.ownerIndexMax = value

                              obj10.ownerIndexMin = items.value.ownerIndexMin

                              obj10.indexMin = items.value.indexMin

                              obj10.indexMax = items.value.indexMax

                              obj10.organizationIndexMin =
                                items.value.organizationIndexMin

                              obj10.organizationIndexMax =
                                items.value.organizationIndexMax

                              handleListChange(
                                [obj10],
                                'ownerIndexMax',
                                items,
                                setSelectedKeys
                              )
                            }
                          }}
                        />
                      </FormControl>
                    </Stack>
                  </Stack>

                  <Stack direction={'row'} spacing={2} alignItems="center">
                    <Stack direction={'row'}>
                      <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                        <InputNumber
                          value={items.value.indexMin}
                          placeholder="indexMin"
                          onChange={(e) => {
                            if (e) {
                              const value = e

                              obj10.indexMin = value

                              obj10.ownerIndexMin = items.value.ownerIndexMin

                              obj10.ownerIndexMax = items.value.ownerIndexMax

                              obj10.indexMax = items.value.indexMax

                              obj10.organizationIndexMin =
                                items.value.organizationIndexMin

                              obj10.organizationIndexMax =
                                items.value.organizationIndexMax

                              handleListChange(
                                [obj10],
                                'indexMin',
                                items,
                                setSelectedKeys
                              )
                            }
                          }}
                        />
                      </FormControl>
                    </Stack>

                    <FormControl sx={{ m: 1 }} size="small">
                      <Typography>{` ≤ ${t('gls')} Index ≤ `}</Typography>
                    </FormControl>

                    <Stack direction={'row'}>
                      <FormControl sx={{ m: 1, width: '5rem' }} size="small">
                        <InputNumber
                          value={items.value.indexMax}
                          placeholder="indexMax"
                          onChange={(e) => {
                            if (e) {
                              const value = e

                              obj10.indexMax = value

                              obj10.indexMin = items.value.indexMin

                              obj10.ownerIndexMin = items.value.ownerIndexMin

                              obj10.ownerIndexMax = items.value.ownerIndexMax

                              obj10.organizationIndexMin =
                                items.value.organizationIndexMin

                              obj10.organizationIndexMax =
                                items.value.organizationIndexMax

                              handleListChange(
                                [obj10],
                                'indexMax',
                                items,
                                setSelectedKeys
                              )
                            }
                          }}
                        />
                      </FormControl>
                    </Stack>
                  </Stack>
                </Stack>
              </FormControl>
            </Stack>
          </FormControl>
        ) : dataIndex === 'dbSNP' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl sx={{ m: 1, width: '12.5rem' }} size="small">
                <Select
                  placeholder={`${t('Choose gene chromosome')}`}
                  optionFilterProp="label"
                  onChange={(value: string[]) => {
                    obj5.chrom = value

                    obj5.start = items.value.posStart

                    obj5.end = items.value.posEnd

                    handleListChange([obj5], 'chrom', items, setSelectedKeys)
                  }}
                  mode="multiple"
                  allowClear
                  options={chromosome.map((item) => {
                    return {
                      value: item.value,
                      label: item.text
                    }
                  })}
                  value={items.value.chrom}
                />
              </FormControl>

              <FormControl sx={{ m: 1, width: '12.5rem' }} size="small">
                <InputNumber
                  value={items.value.posStart}
                  placeholder={t('posStart')}
                  onChange={(e) => {
                    if (e) {
                      const value = e

                      obj5.start = value

                      obj5.chrom = items.value.chrom

                      obj5.end = items.value.posEnd

                      handleListChange(
                        [obj5],
                        'posStart',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                  style={{
                    width: '100%'
                  }}
                />
              </FormControl>

              <FormControl sx={{ m: 1, width: '12.5rem' }} size="small">
                <InputNumber
                  style={{
                    width: '100%'
                  }}
                  value={items.value.posEnd}
                  placeholder={t('posEnd')}
                  onChange={(e) => {
                    if (e) {
                      const value = e

                      obj5.end = value

                      obj5.chrom = items.value.chrom

                      obj5.start = items.value.posStart

                      handleListChange([obj5], 'posEnd', items, setSelectedKeys)
                    }
                  }}
                />
              </FormControl>
            </Stack>
          </FormControl>
        ) : dataIndex === 'f_score' ? (
          <Stack
            direction={'row'}
            spacing={2}
            width={300}
            justifyContent="center"
            alignItems={'center'}
          >
            <FormControl sx={{ m: 1 }} size="small">
              <Typography>{t('F score')}</Typography>
            </FormControl>

            <FormControl sx={{ m: 1 }} size="small">
              <Select
                placeholder={t('F score')}
                onChange={(e) => {
                  obj.operator = e

                  obj.value = items.value.fscoreValue

                  handleListChange(
                    [obj],
                    'fscoreOperator',
                    items,
                    setSelectedKeys
                  )
                }}
                value={items.value.fscoreOperator}
                options={[
                  { value: 'gte', label: '≥' },
                  { value: 'lte', label: '≤' }
                ]}
              />
            </FormControl>

            <FormControl sx={{ m: 1, width: '8rem' }} size="small">
              <InputNumber
                value={items.value.fscoreValue}
                placeholder="fscoreValue"
                onChange={(e) => {
                  if (e) {
                    const value = e

                    obj.value = value

                    obj.operator = items.value.fscoreOperator

                    handleListChange(
                      [obj],
                      'fscoreValue',
                      items,
                      setSelectedKeys
                    )
                  }
                }}
                style={{
                  width: '100%'
                }}
              />
            </FormControl>
          </Stack>
        ) : dataIndex === 'g_score' ? (
          <Stack
            direction={'row'}
            spacing={2}
            width={300}
            justifyContent="center"
            alignItems={'center'}
          >
            <FormControl sx={{ m: 1 }} size="small">
              <Typography>{t('G score')}</Typography>
            </FormControl>

            <FormControl sx={{ m: 1 }} size="small">
              <Select
                placeholder={t('G score')}
                value={items.value.gscoreOperator}
                onChange={(e) => {
                  obj.operator = e

                  obj.value = items.value.gscoreValue

                  handleListChange(
                    [obj],
                    'gscoreOperator',
                    items,
                    setSelectedKeys
                  )
                }}
                options={[
                  { value: 'gte', label: '≥' },
                  { value: 'lte', label: '≤' }
                ]}
              />
            </FormControl>

            <FormControl sx={{ m: 1, width: '8rem' }} size="small">
              <InputNumber
                value={items.value.gscoreValue}
                placeholder="gscoreValue"
                onChange={(e) => {
                  if (e) {
                    const value = e

                    obj.value = value

                    obj.operator = items.value.gscoreOperator

                    handleListChange(
                      [obj],
                      'gscoreValue',
                      items,
                      setSelectedKeys
                    )
                  }
                }}
                style={{
                  width: '100%'
                }}
              />
            </FormControl>
          </Stack>
        ) : dataIndex === 'start' ? (
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'column',
              alignItems: 'center'
            }}
            size="small"
          >
            <Stack
              direction={'row'}
              spacing={2}
              width={300}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl sx={{ m: 1 }} size="small">
                <Typography>{t('posStart')}</Typography>
              </FormControl>

              <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                <InputNumber
                  value={items.value.posStart}
                  placeholder="posStart"
                  onChange={(e) => {
                    if (e) {
                      const value = e

                      obj2.start = value

                      obj2.end = items.value.posEnd

                      handleListChange(
                        [obj2],
                        'posStart',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                  style={{
                    width: '100%'
                  }}
                />
              </FormControl>
            </Stack>

            <Stack
              direction={'row'}
              spacing={2}
              width={300}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl sx={{ m: 1 }} size="small">
                <Typography>{t('posEnd')}</Typography>
              </FormControl>

              <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                <InputNumber
                  value={items.value.posEnd}
                  placeholder="posEnd"
                  onChange={(e) => {
                    if (e) {
                      const value = e

                      obj2.end = value

                      obj2.start = items.value.posStart

                      handleListChange([obj2], 'posEnd', items, setSelectedKeys)
                    }
                  }}
                  style={{
                    width: '100%'
                  }}
                />
              </FormControl>
            </Stack>
          </FormControl>
        ) : dataIndex === 'CN' ? (
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'column',
              alignItems: 'center'
            }}
            size="small"
          >
            <Stack
              direction={'row'}
              spacing={2}
              width={300}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl sx={{ m: 1 }} size="small">
                <Typography>{t('Copy Number(CN)')}</Typography>
              </FormControl>

              <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                <InputNumber
                  value={items.value.CopyNumber}
                  placeholder="CopyNumber"
                  onChange={(e) => {
                    if (e) {
                      const value = e

                      obj8.CopyNumber = value

                      handleListChange(
                        [obj8],
                        'CopyNumber',
                        items,
                        setSelectedKeys
                      )
                    }
                  }}
                  style={{
                    width: '100%'
                  }}
                />
              </FormControl>
            </Stack>
          </FormControl>
        ) : dataIndex === 'SpliceAI_max_score' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <Stack
                direction={'row'}
                spacing={2}
                width={400}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('Revel')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('Revel')}
                    value={items.value.revelOperator}
                    onChange={(e) => {
                      obj3.revelOperator = e

                      obj3.revelValue = items.value.revelValue

                      obj3.alphaMOperator = items.value.alphaMOperator

                      obj3.alphaMValue = items.value.alphaMValue

                      obj3.spliceAIOperator = items.value.spliceAIOperator

                      obj3.spliceAIValue = items.value.spliceAIValue

                      handleListChange(
                        [obj3],
                        'revelOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte.', label: '≥包含无' },
                      { value: 'lte.', label: '≤包含无' },
                      { value: 'gte', label: '≥不包含无' },
                      { value: 'lte', label: '≤不包含无' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.revelValue}
                    placeholder="revelValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj3.revelValue = value

                        obj3.revelOperator = items.value.revelOperator

                        obj3.alphaMOperator = items.value.alphaMOperator

                        obj3.alphaMValue = items.value.alphaMValue

                        obj3.spliceAIOperator = items.value.spliceAIOperator

                        obj3.spliceAIValue = items.value.spliceAIValue

                        handleListChange(
                          [obj3],
                          'revelValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction={'row'}
                spacing={2}
                width={400}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('AlphaM')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('AlphaM')}
                    value={items.value.alphaMOperator}
                    onChange={(e) => {
                      obj3.alphaMOperator = e

                      obj3.revelOperator = items.value.revelOperator

                      obj3.revelValue = items.value.revelValue

                      obj3.alphaMValue = items.value.alphaMValue

                      obj3.spliceAIOperator = items.value.spliceAIOperator

                      obj3.spliceAIValue = items.value.spliceAIValue

                      handleListChange(
                        [obj3],
                        'alphaMOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte.', label: '≥包含无' },
                      { value: 'lte.', label: '≤包含无' },
                      { value: 'gte', label: '≥不包含无' },
                      { value: 'lte', label: '≤不包含无' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.alphaMValue}
                    placeholder="alphaMValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj3.alphaMValue = value

                        obj3.revelOperator = items.value.revelOperator

                        obj3.revelValue = items.value.revelValue

                        obj3.alphaMOperator = items.value.alphaMOperator

                        obj3.spliceAIOperator = items.value.spliceAIOperator

                        obj3.spliceAIValue = items.value.spliceAIValue

                        handleListChange(
                          [obj3],
                          'alphaMValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction={'row'}
                spacing={2}
                width={400}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('SpliceAI')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 18 }} size="small">
                  <Select
                    placeholder={t('SpliceAI')}
                    value={items.value.spliceAIOperator}
                    onChange={(e) => {
                      obj3.spliceAIOperator = e

                      obj3.revelOperator = items.value.revelOperator

                      obj3.revelValue = items.value.revelValue

                      obj3.alphaMOperator = items.value.alphaMOperator

                      obj3.alphaMValue = items.value.alphaMValue

                      obj3.spliceAIValue = items.value.spliceAIValue

                      handleListChange(
                        [obj3],
                        'spliceAIOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte.', label: '≥包含无' },
                      { value: 'lte.', label: '≤包含无' },
                      { value: 'gte', label: '≥不包含无' },
                      { value: 'lte', label: '≤不包含无' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.spliceAIValue}
                    placeholder="spliceAIValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj3.spliceAIValue = value

                        obj3.revelOperator = items.value.revelOperator

                        obj3.revelValue = items.value.revelValue

                        obj3.alphaMOperator = items.value.alphaMOperator

                        obj3.alphaMValue = items.value.alphaMValue

                        obj3.spliceAIOperator = items.value.spliceAIOperator

                        handleListChange(
                          [obj3],
                          'spliceAIValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </FormControl>
        ) : dataIndex === 'scip_gnomAD&Clinvar' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <Stack
                direction={'row'}
                spacing={2}
                width={300}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('Overlap Rate')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('Overlap Rate')}
                    value={items.value.scipOverlapRateOperator}
                    onChange={(e) => {
                      obj13.scipOverlapRateOperator = e

                      obj13.scipOverlapRateValue =
                        items.value.scipOverlapRateValue

                      obj13.scipGnomAdOverlapRateOperator =
                        items.value.scipGnomAdOverlapRateOperator

                      obj13.scipGnomAdOverlapRateValue =
                        items.value.scipGnomAdOverlapRateValue

                      obj13.scipClinvarOverlapRateOperator =
                        items.value.scipClinvarOverlapRateOperator

                      obj13.scipClinvarOverlapRateValue =
                        items.value.scipClinvarOverlapRateValue

                      handleListChange(
                        [obj13],
                        'scipOverlapRateOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.scipOverlapRateValue}
                    placeholder="scipOverlapRateValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj13.scipOverlapRateValue = value

                        obj13.scipOverlapRateOperator =
                          items.value.scipOverlapRateOperator

                        obj13.scipGnomAdOverlapRateOperator =
                          items.value.scipGnomAdOverlapRateOperator

                        obj13.scipGnomAdOverlapRateValue =
                          items.value.scipGnomAdOverlapRateValue

                        obj13.scipClinvarOverlapRateOperator =
                          items.value.scipClinvarOverlapRateOperator

                        obj13.scipClinvarOverlapRateValue =
                          items.value.scipClinvarOverlapRateValue

                        handleListChange(
                          [obj13],
                          'scipOverlapRateValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction={'row'}
                spacing={2}
                width={300}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('GnomAd')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('GnomAd')}
                    value={items.value.scipGnomAdOverlapRateOperator}
                    onChange={(e) => {
                      obj13.scipGnomAdOverlapRateOperator = e

                      obj13.scipOverlapRateOperator =
                        items.value.scipOverlapRateOperator

                      obj13.scipOverlapRateValue =
                        items.value.scipOverlapRateValue

                      obj13.scipGnomAdOverlapRateValue =
                        items.value.scipGnomAdOverlapRateValue

                      obj13.scipClinvarOverlapRateOperator =
                        items.value.scipClinvarOverlapRateOperator

                      obj13.scipClinvarOverlapRateValue =
                        items.value.scipClinvarOverlapRateValue

                      handleListChange(
                        [obj13],
                        'scipGnomAdOverlapRateOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.scipGnomAdOverlapRateValue}
                    placeholder="scipGnomAdOverlapRateValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj13.scipGnomAdOverlapRateValue = value

                        obj13.scipOverlapRateOperator =
                          items.value.scipOverlapRateOperator

                        obj13.scipOverlapRateValue =
                          items.value.scipOverlapRateValue

                        obj13.scipGnomAdOverlapRateOperator =
                          items.value.scipGnomAdOverlapRateOperator

                        obj13.scipClinvarOverlapRateOperator =
                          items.value.scipClinvarOverlapRateOperator

                        obj13.scipClinvarOverlapRateValue =
                          items.value.scipClinvarOverlapRateValue

                        handleListChange(
                          [obj13],
                          'scipGnomAdOverlapRateValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction={'row'}
                spacing={2}
                width={300}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('Clinvar')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 18 }} size="small">
                  <Select
                    placeholder={t('Clinvar')}
                    value={items.value.scipClinvarOverlapRateOperator}
                    onChange={(e) => {
                      obj13.scipClinvarOverlapRateOperator = e

                      obj13.scipOverlapRateOperator =
                        items.value.scipOverlapRateOperator

                      obj13.scipOverlapRateValue =
                        items.value.scipOverlapRateValue

                      obj13.scipGnomAdOverlapRateOperator =
                        items.value.scipGnomAdOverlapRateOperator

                      obj13.scipClinvarOverlapRateValue =
                        items.value.scipClinvarOverlapRateValue

                      obj13.scipClinvarOverlapRateValue =
                        items.value.spliceAIValue

                      handleListChange(
                        [obj13],
                        'scipClinvarOverlapRateOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.scipClinvarOverlapRateValue}
                    placeholder="scipClinvarOverlapRateValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj13.scipClinvarOverlapRateValue = value

                        obj13.scipOverlapRateOperator =
                          items.value.scipOverlapRateOperator

                        obj13.scipOverlapRateValue =
                          items.value.scipOverlapRateValue

                        obj13.scipGnomAdOverlapRateOperator =
                          items.value.scipGnomAdOverlapRateOperator

                        obj13.scipGnomAdOverlapRateValue =
                          items.value.scipGnomAdOverlapRateValue

                        obj13.scipClinvarOverlapRateOperator =
                          items.value.scipClinvarOverlapRateOperator

                        handleListChange(
                          [obj13],
                          'scipClinvarOverlapRateValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </FormControl>
        ) : dataIndex === 'scip_Clingen' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <FormControl sx={{ m: 1, width: '12.5rem' }} size="small">
              <Autocomplete
                size="small"
                multiple
                id="tags-outlined"
                options={ScipClingenDose}
                getOptionLabel={(option: any) => option.title}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputLabelProps={{ shrink: true }}
                    label={t('ScipClingenHaploIn')}
                  />
                )}
                value={items.value.scipClingenHaploIn}
                onChange={(e: any, v: any) => {
                  obj15.scipClingenHaploIn = unique(
                    v.map((item: any) => item),
                    'key'
                  )

                  obj15.scipClingenTriploIn = items.value.scipClingenTriploIn

                  handleListChange(
                    [obj15],
                    'scipClingenHaploIn',
                    items,
                    setSelectedKeys
                  )
                }}
              />
            </FormControl>

            <FormControl sx={{ m: 1, width: '12.5rem' }} size="small">
              <Autocomplete
                size="small"
                multiple
                id="tags-outlined"
                options={ScipClingenDose}
                getOptionLabel={(option: any) => option.title}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputLabelProps={{ shrink: true }}
                    label={t('scipClingenTriploIn')}
                  />
                )}
                value={items.value.scipClingenTriploIn}
                onChange={(e: any, v: any) => {
                  obj15.scipClingenTriploIn = unique(
                    v.map((item: any) => item),
                    'key'
                  )

                  obj15.scipClingenHaploIn = items.value.scipClingenHaploIn

                  handleListChange(
                    [obj15],
                    'scipClingenTriploIn',
                    items,
                    setSelectedKeys
                  )
                }}
              />
            </FormControl>
          </FormControl>
        ) : dataIndex === 'scip_Geneinfo' ? (
          <FormControl sx={{ m: 1, width: '400px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <Stack
                direction={'row'}
                spacing={2}
                width={400}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('Gene Overlap Rate')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('Gene Overlap Rate')}
                    value={items.value.scipGeneOverlapRateOperator}
                    onChange={(e) => {
                      obj14.scipGeneOverlapRateOperator = e

                      obj14.scipGeneOverlapRateValue =
                        items.value.scipGeneOverlapRateValue

                      obj14.scipGenePliOperator =
                        items.value.scipGenePliOperator

                      obj14.scipGenePliValue = items.value.scipGenePliValue

                      obj14.scipGeneLoeufOperator =
                        items.value.scipGeneLoeufOperator

                      obj14.scipGeneLoeufValue = items.value.scipGeneLoeufValue

                      obj14.scipGeneHiIn = items.value.scipGeneHiIn

                      obj14.scipGeneTsIn = items.value.scipGeneTsIn

                      handleListChange(
                        [obj14],
                        'scipGeneOverlapRateOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.scipGeneOverlapRateValue}
                    placeholder="scipGeneOverlapRateValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj14.scipGeneOverlapRateValue = value

                        obj14.scipGeneOverlapRateOperator =
                          items.value.scipGeneOverlapRateOperator

                        obj14.scipGenePliOperator =
                          items.value.scipGenePliOperator

                        obj14.scipGenePliValue = items.value.scipGenePliValue

                        obj14.scipGeneLoeufOperator =
                          items.value.scipGeneLoeufOperator

                        obj14.scipGeneLoeufValue =
                          items.value.scipGeneLoeufValue

                        obj14.scipGeneHiIn = items.value.scipGeneHiIn

                        obj14.scipGeneTsIn = items.value.scipGeneTsIn

                        handleListChange(
                          [obj14],
                          'scipGeneOverlapRateValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction={'row'}
                spacing={2}
                width={400}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('Gene Pli')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 1 }} size="small">
                  <Select
                    placeholder={t('Gene Pli')}
                    value={items.value.scipGenePliOperator}
                    onChange={(e) => {
                      obj14.scipGenePliOperator = e

                      obj14.scipGeneOverlapRateOperator =
                        items.value.scipGeneOverlapRateOperator

                      obj14.scipGeneOverlapRateValue =
                        items.value.scipGeneOverlapRateValue

                      obj14.scipGenePliValue = items.value.scipGenePliValue

                      obj14.scipGeneLoeufOperator =
                        items.value.scipGeneLoeufOperator

                      obj14.scipGeneLoeufValue = items.value.scipGeneLoeufValue

                      obj14.scipGeneHiIn = items.value.scipGeneHiIn

                      obj14.scipGeneTsIn = items.value.scipGeneTsIn

                      handleListChange(
                        [obj14],
                        'scipGenePliOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.scipGenePliValue}
                    placeholder="scipGenePliValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj14.scipGenePliValue = value

                        obj14.scipGeneOverlapRateOperator =
                          items.value.scipGeneOverlapRateOperator

                        obj14.scipGeneOverlapRateValue =
                          items.value.scipGeneOverlapRateValue

                        obj14.scipGenePliOperator =
                          items.value.scipGenePliOperator

                        obj14.scipGeneLoeufOperator =
                          items.value.scipGeneLoeufOperator

                        obj14.scipGeneLoeufValue =
                          items.value.scipGeneLoeufValue

                        obj14.scipGeneHiIn = items.value.scipGeneHiIn

                        obj14.scipGeneTsIn = items.value.scipGeneTsIn

                        handleListChange(
                          [obj14],
                          'scipGenePliValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction={'row'}
                spacing={2}
                width={400}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('Gene Loeuf')}</Typography>
                </FormControl>

                <FormControl sx={{ m: 18 }} size="small">
                  <Select
                    placeholder={t('Gene Loeuf')}
                    value={items.value.scipGeneLoeufOperator}
                    onChange={(e) => {
                      obj14.scipGeneLoeufOperator = e

                      obj14.scipGeneOverlapRateOperator =
                        items.value.scipGeneOverlapRateOperator

                      obj14.scipGeneOverlapRateValue =
                        items.value.scipGeneOverlapRateValue

                      obj14.scipGenePliOperator =
                        items.value.scipGenePliOperator

                      obj14.scipGeneLoeufValue = items.value.scipGeneLoeufValue

                      obj14.scipGeneLoeufValue = items.value.spliceAIValue

                      obj14.scipGeneHiIn = items.value.scipGeneHiIn

                      obj14.scipGeneTsIn = items.value.scipGeneTsIn

                      handleListChange(
                        [obj14],
                        'scipGeneLoeufOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  />
                </FormControl>

                <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                  <InputNumber
                    value={items.value.scipGeneLoeufValue}
                    placeholder="scipGeneLoeufValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj14.scipGeneLoeufValue = value

                        obj14.scipGeneOverlapRateOperator =
                          items.value.scipGeneOverlapRateOperator

                        obj14.scipGeneOverlapRateValue =
                          items.value.scipGeneOverlapRateValue

                        obj14.scipGenePliOperator =
                          items.value.scipGenePliOperator

                        obj14.scipGenePliValue = items.value.scipGenePliValue

                        obj14.scipGeneLoeufOperator =
                          items.value.scipGeneLoeufOperator

                        obj14.scipGeneHiIn = items.value.scipGeneHiIn

                        obj14.scipGeneTsIn = items.value.scipGeneTsIn

                        handleListChange(
                          [obj14],
                          'scipGeneLoeufValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </FormControl>
        ) : dataIndex === 'gnomAD_exome_ALL' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <ToggleButtonGroup
                aria-label="device"
                size="small"
                color="primary"
                value={items.value.gnomAD}
                onChange={(e, value) => {
                  obj6.gnomAD = value

                  obj6.gnomADOperator = items.value.gnomADOperator

                  obj6.gnomADValue = items.value.gnomADValue

                  handleListChange([obj6], 'gnomAD', items, setSelectedKeys)
                }}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  width: '100%',
                  justifyContent: 'center'
                }}
              >
                {props.analysisInfo.pipelineVersion ? (
                  <>
                    <ToggleButton value="gnomade_af" aria-label="gADE_ALL">
                      gADE_ALL
                    </ToggleButton>

                    <ToggleButton value="gnomadg_af" aria-label="gADG_ALL">
                      gADG_ALL
                    </ToggleButton>

                    <ToggleButton value="wbbc_af" aria-label="WBBC">
                      WBBC
                    </ToggleButton>
                  </>
                ) : (
                  <>
                    <ToggleButton value="gnomAD_exome_ALL" aria-label="gAD_ALL">
                      gAD_ALL
                    </ToggleButton>

                    <ToggleButton value="gnomAD_exome_EAS" aria-label="gAD_EAS">
                      gAD_EAS
                    </ToggleButton>
                  </>
                )}
              </ToggleButtonGroup>

              <Stack
                direction={'row'}
                spacing={2}
                width={300}
                justifyContent="center"
                alignItems={'center'}
              >
                <FormControl sx={{ m: 1 }} size="small">
                  <Typography>{t('operator')}</Typography>
                </FormControl>

                <FormControl
                  sx={{ m: 1, width: '5rem', marginTop: 2 }}
                  size="small"
                >
                  <Select
                    placeholder={t('gnomADOperator')}
                    value={items.value.gnomADOperator}
                    onChange={(e) => {
                      obj6.gnomADOperator = e

                      obj6.gnomAD = items.value.gnomAD

                      obj6.gnomADValue = items.value.gnomADValue

                      handleListChange(
                        [obj6],
                        'gnomADOperator',
                        items,
                        setSelectedKeys
                      )
                    }}
                    options={[
                      { value: 'gte', label: '≥' },
                      { value: 'lte', label: '≤' }
                    ]}
                  ></Select>
                </FormControl>

                <FormControl
                  sx={{ m: 1, width: '8rem', marginTop: 2 }}
                  size="small"
                >
                  <InputNumber
                    value={items.value.gnomADValue}
                    placeholder="gnomADValue"
                    onChange={(e) => {
                      if (e) {
                        const value = e

                        obj6.gnomADValue = value

                        obj6.gnomAD = items.value.gnomAD

                        obj6.gnomADOperator = items.value.gnomADOperator

                        handleListChange(
                          [obj6],
                          'gnomADValue',
                          items,
                          setSelectedKeys
                        )
                      }
                    }}
                    style={{
                      width: '100%'
                    }}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </FormControl>
        ) : dataIndex === 'avgReadDepth' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl
                sx={{
                  m: 1,
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '15rem'
                }}
                size="small"
              >
                <Stack direction={'row'}>
                  <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                    <Typography>AvgReadDepth ≥</Typography>
                  </FormControl>

                  <FormControl sx={{ m: 1, width: '6rem' }} size="small">
                    <InputNumber
                      value={items.value.Min}
                      placeholder="Min"
                      onChange={(e) => {
                        if (e) {
                          const value = e

                          obj9.Min = value

                          obj9.Max = items.value.Max

                          handleListChange(
                            [obj9],
                            'Min',
                            items,
                            setSelectedKeys
                          )
                        }
                      }}
                    />
                  </FormControl>
                </Stack>

                <Stack direction={'row'}>
                  <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                    <Typography>AvgReadDepth ≤</Typography>
                  </FormControl>

                  <FormControl sx={{ m: 1, width: '6rem' }} size="small">
                    <InputNumber
                      value={items.value.Max}
                      placeholder="Max"
                      onChange={(e) => {
                        if (e) {
                          const value = e

                          obj9.Max = value

                          obj9.Min = items.value.Min

                          handleListChange(
                            [obj9],
                            'Max',
                            items,
                            setSelectedKeys
                          )
                        }
                      }}
                    />
                  </FormControl>
                </Stack>
              </FormControl>
            </Stack>
          </FormControl>
        ) : dataIndex === 'fold80' ? (
          <FormControl sx={{ m: 1, width: '300px' }} size="small">
            <Stack
              direction={'column'}
              spacing={1}
              justifyContent="center"
              alignItems={'center'}
            >
              <FormControl
                sx={{
                  m: 1,
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '15rem'
                }}
                size="small"
              >
                <Stack direction={'row'}>
                  <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                    <Typography>fold80 ≥</Typography>
                  </FormControl>

                  <FormControl sx={{ m: 1, width: '6rem' }} size="small">
                    <InputNumber
                      value={items.value.Min}
                      placeholder="Min"
                      onChange={(e) => {
                        if (e) {
                          const value = e

                          obj11.Min = value

                          obj11.Max = items.value.Max

                          handleListChange(
                            [obj11],
                            'Min',
                            items,
                            setSelectedKeys
                          )
                        }
                      }}
                    />
                  </FormControl>
                </Stack>

                <Stack direction={'row'}>
                  <FormControl sx={{ m: 1, width: '8rem' }} size="small">
                    <Typography>fold80 ≤</Typography>
                  </FormControl>

                  <FormControl sx={{ m: 1, width: '6rem' }} size="small">
                    <InputNumber
                      value={items.value.Max}
                      placeholder="Max"
                      onChange={(e) => {
                        if (e) {
                          const value = e

                          obj11.Max = value

                          obj11.Min = items.value.Min

                          handleListChange(
                            [obj11],
                            'Max',
                            items,
                            setSelectedKeys
                          )
                        }
                      }}
                    />
                  </FormControl>
                </Stack>
              </FormControl>
            </Stack>
          </FormControl>
        ) : (
          ''
        )}

        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters)

              setConfig(filterConfig)
            }}
            size="small"
          >
            {t('reset')}
          </Button>

          <Button
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false })

              props.setConfig(config)

              //
            }}
          >
            {t('Filter')}
          </Button>

          <Button
            size="small"
            onClick={() => {
              close()
            }}
          >
            {t('close')}
          </Button>
        </Stack>
      </div>
    </Box>
  )
}

export default FilterItem
